import { utils } from '@/utils';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { EndpointResponse } from '@/types/endpoints';
import type { AxiosRequestConfig } from 'axios';

export type ServicePlatformCallsHealth = {
  id: number;
  created_at: string;
  updated_at: string;
  store_name: string;
  channel_name: string;
  kernel_command: string;
  subscription_id: number;
  failed: number;
  processed: number;
  processed_greater_than_100_milliseconds: number;
  fail_rate: number;
  average_processing_seconds: number;
  max_processing_seconds: number;
  exceeded_max_runtime: number;
};

export type GetServicePlatformCallsHealthParams = {
  page?: number;
  limit?: number;
  subscription_id?: number;
  store_name?: string;
  channel_name?: string;
  include_test_stores?: boolean;
  groupby?: string;
  orderby_field?: string;
  orderby?: 'asc' | 'desc';
  hours?: number | 'all';
  skipHours?: boolean;
};

export type ServicePlatformCallsHealthResponse = {
  data: ServicePlatformCallsHealth[] | Record<string, ServicePlatformCallsHealth[]>;
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
};

type HealthSearchRequestParams = {
  page: number;
  limit: number;
  query?: string;
};

type HealthSearchQueryObj = {
  $groupby?: string;
  hours?: number | 'all';
  channel_name?: string;
  store_name?: string;
  subscription_id?: number;
  include_test_stores?: true;
  [orderbyField: string]: string | number | true | { $orderby: 'asc' | 'desc' } | undefined;
};

export const getServicePlatformCallsHealth = async (
  scope: RequestScope,
  params: GetServicePlatformCallsHealthParams = {},
  options: Pick<AxiosRequestConfig, 'operationRequirement' | 'signal'> = {}
): Promise<EndpointResponse<ServicePlatformCallsHealthResponse>> => {
  try {
    const page = params?.page || 1;
    const limit = params?.limit || 100;

    const requestParams: HealthSearchRequestParams = {
      page,
      limit,
    };

    const queryObj: HealthSearchQueryObj = {};
    if (params?.groupby) {
      queryObj.$groupby = params.groupby;
    }
    if (params?.orderby && params?.orderby_field) {
      queryObj[params.orderby_field] = { $orderby: params.orderby };
    }
    if (!params?.skipHours) {
      queryObj.hours = params?.hours ?? 6;
    }
    if (params?.channel_name) {
      queryObj.channel_name = params.channel_name;
    }

    // Include store filter in query only when a specific store is selected.
    // For "All Stores", omit store_name entirely.
    if (params && Object.prototype.hasOwnProperty.call(params, 'store_name')) {
      const normalizedStoreName = params.store_name?.trim();
      if (normalizedStoreName) {
        queryObj.store_name = normalizedStoreName;
      }
    }

    // Include subscription filter in query.
    if (params?.subscription_id) {
      queryObj.subscription_id = params.subscription_id;
    }

    // Include test stores only when explicitly enabled.
    if (params?.include_test_stores) {
      queryObj.include_test_stores = true;
    }

    if (Object.keys(queryObj).length > 0) {
      requestParams.query = JSON.stringify(queryObj);
    }

    const result = await scopedChannelApi(scope).get<ServicePlatformCallsHealthResponse>('spc/health/search', {
      ...options,
      params: requestParams,
    });

    const healthData = result.data?.data ?? [];
    const total = result.data?.total || 0;
    const current_page = result.data?.current_page || 1;
    const per_page = result.data?.per_page || limit;
    const last_page = result.data?.last_page || 1;

    let num_results = 0;
    if (Array.isArray(healthData)) {
      num_results = healthData.length;
    } else if (typeof healthData === 'object' && healthData !== null) {
      num_results = Object.values(healthData).reduce((sum: number, group: ServicePlatformCallsHealth[] | unknown) => {
        return sum + (Array.isArray(group) ? group.length : 0);
      }, 0);
    }

    return {
      result: {
        data: healthData,
        total,
        current_page,
        per_page,
        last_page,
      },
      status: result.status,
      execution_time: 0,
      num_results,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
